import React, { useState, useEffect, useRef, useCallback } from 'react';
import ModalShell from '@/components/ModalShell';
import SectionIcon from '@/components/SectionIcon';
import QrScanModal from '@/components/QrScanModal';
import { I } from '@/lib/icons';
import { CATEGORIES, DPH_RATES } from '@/lib/sections';
import { useWorkspace, useExpenses } from '@/hooks/useData';
import { useCloseOnWorkspaceSwitch } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/Toast';
import { toIsoDate } from '@/lib/format';
import { supabase } from '@/lib/supabase';
import { lookupEkasaReceipt } from '@/lib/ekasa';
import { fileToBase64DataUrl } from '@/lib/imageUtils';
import { extractFromImage } from '@/lib/extractReceipt';

const STORAGE_BUCKET = 'expense-docs';

export default function NewExpenseModal({ open, onClose, expense }) {
  const { workspace } = useWorkspace();
  const { user } = useAuth();
  const { create, update } = useExpenses(workspace?.id);
  const toast = useToast();
  const isEdit = !!expense;
  const fileInputRef = useRef(null);

  useCloseOnWorkspaceSwitch(open, onClose);

  const [form, setForm] = useState({
    vendor_name: '', vendor_ico: '', amount: '', dph_rate: 0,
    date: toIsoDate(), category: 'other', description: '',
    paid_via: 'transfer',
  });
  const [aiHints, setAiHints] = useState({}); // { vendor_name: true, amount: true, ... }
  const [docUrl, setDocUrl] = useState('');
  const [docPreview, setDocPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [busy, setBusy] = useState(''); // '' | 'ekasa' | 'upload' | 'ai'

  // HI-03: po zatvorení modalu (vrátane prepnutia firmy) zhasni busy indikátor.
  // In-flight upload/extrakcia už neovplyvní UI.
  useEffect(() => {
    if (!open) setBusy('');
  }, [open]);

  useEffect(() => {
    if (open) {
      setAiHints({});
      setDocPreview('');
      setDocUrl(expense?.document_url || '');
      if (expense) setForm({
        vendor_name: expense.vendor_name || '',
        vendor_ico: expense.vendor_ico || '',
        amount: String(expense.amount ?? ''),
        dph_rate: expense.dph_rate ?? 0,
        date: expense.date || toIsoDate(),
        category: expense.category || 'other',
        description: expense.description || '',
        paid_via: expense.paid_via || 'transfer',
      });
      else setForm({
        vendor_name: '', vendor_ico: '', amount: '', dph_rate: workspace?.default_dph_rate || 0,
        date: toIsoDate(), category: 'other', description: '',
        paid_via: 'transfer',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, expense]);

  const set = (k) => (e) => {
    setForm(f => ({ ...f, [k]: typeof e === 'object' ? e.target.value : e }));
    if (aiHints[k]) setAiHints(h => ({ ...h, [k]: false }));
  };

  const fillFromExtraction = (data, source = 'ai') => {
    const next = { ...form };
    const hints = { ...aiHints };
    if (data.vendor) { next.vendor_name = data.vendor; hints.vendor_name = source; }
    if (data.ico) { next.vendor_ico = data.ico; hints.vendor_ico = source; }
    if (data.amount) { next.amount = String(data.amount); hints.amount = source; }
    if (data.date) { next.date = data.date; hints.date = source; }
    if (data.vatRate != null) { next.dph_rate = data.vatRate; hints.dph_rate = source; }
    if (Array.isArray(data.items) && data.items.length) {
      const summary = data.items.slice(0, 5).map(it => it.name).filter(Boolean).join(', ');
      if (summary && !next.description) { next.description = summary; hints.description = source; }
    }
    setForm(next);
    setAiHints(hints);
  };

  const handleQrScan = useCallback(async (qrText) => {
    setQrOpen(false);
    setBusy('ekasa');
    try {
      const data = await lookupEkasaReceipt(qrText);
      fillFromExtraction(data, 'ekasa');
      toast.success('Blok načítaný z eKasa.');
    } catch (e) {
      toast.error(e.message || 'eKasa lookup zlyhal.');
    } finally {
      setBusy('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  if (!open) return null;

  const handlePhotoFile = async (file) => {
    if (!file) return;
    if (!user?.id || !workspace?.id) {
      toast.error('Najprv sa prihlás a vyber firmu.');
      return;
    }

    // HI-03: zachyť ID firmy v moment štartu uploadu. Ak sa firma medzitým
    // prepne (alebo modal zatvorí), zahodíme výsledok a nebudeme prepisovať
    // formulár ktorý už patrí inej firme.
    const startWsId = workspace.id;

    let dataUrl;
    try { dataUrl = await fileToBase64DataUrl(file); }
    catch (e) { toast.error(e.message); return; }
    setDocPreview(dataUrl);

    setBusy('upload');
    const ext = (file.name?.split('.').pop() || 'jpg').toLowerCase();
    const path = `${user.id}/${startWsId}/${Date.now()}.${ext}`;
    try {
      const { error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(path, file, { contentType: file.type || 'image/jpeg', upsert: false });
      if (error) throw error;
      // HI-03: po networke skontroluj, či sa medzitým firma neprepla. Ak áno,
      // upload ostal v storage A — to je akceptovateľné (orphan), kľúčové je
      // že NEzapíšeme docUrl/toast do nového kontextu firmy B.
      if (workspace?.id !== startWsId) return;
      const { data: pub } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
      setDocUrl(pub?.publicUrl || path);
      toast.success('Doklad nahraný.');
    } catch (e) {
      if (workspace?.id !== startWsId) return;
      toast.error('Upload do storage zlyhal: ' + (e.message || e));
      setBusy('');
      return;
    }

    setBusy('ai');
    try {
      const data = await extractFromImage(file);
      // HI-03: drop AI výsledok ak sa firma medzitým prepla — neprepíšeme
      // formulár ktorý už patrí inej firme.
      if (workspace?.id !== startWsId) return;
      if (data.confidence > 0.2 && (data.amount || data.vendor)) {
        const source = data.source === 'tesseract' ? 'ocr' : 'ai';
        fillFromExtraction(data, source);
        const label = data.source === 'tesseract' ? 'OCR' : 'AI';
        toast.success(`${label} vyplnila polia (presnosť ${Math.round(data.confidence * 100)} %).`);
      } else {
        toast.error('Nepodarilo sa prečítať doklad — zadaj ručne.');
      }
    } catch (e) {
      if (workspace?.id !== startWsId) return;
      toast.error('Extrakcia zlyhala: ' + e.message);
    } finally {
      if (workspace?.id === startWsId) setBusy('');
    }
  };

  const submit = async () => {
    const amount = Number(String(form.amount).replace(',', '.'));
    if (!amount || amount <= 0) { toast.error('Zadaj sumu.'); return; }
    if (!form.date) { toast.error('Zadaj dátum.'); return; }
    setSaving(true);
    try {
      const dphAmount = (amount * Number(form.dph_rate)) / (100 + Number(form.dph_rate));
      const payload = {
        vendor_name: form.vendor_name.trim() || null,
        vendor_ico: form.vendor_ico.trim() || null,
        amount,
        dph_rate: Number(form.dph_rate),
        dph_amount: dphAmount,
        date: form.date,
        category: form.category,
        description: form.description.trim() || null,
        paid_via: form.paid_via,
        document_url: docUrl || null,
      };
      if (isEdit) {
        await update(expense.id, payload);
        toast.success('Uložené.');
      } else {
        await create(payload);
        toast.success('Výdavok pridaný.');
      }
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const busyLabel =
    busy === 'ekasa' ? 'Načítavam blok z eKasa...' :
    busy === 'upload' ? 'Nahrávam doklad...' :
    busy === 'ai' ? 'AI číta doklad...' : '';

  return (
    <>
      <ModalShell open={open} onClose={onClose}
        title={isEdit ? 'Detail výdavku' : 'Nový výdavok'} maxWidth={620}
        rightAction={
          <button onClick={submit} disabled={saving || !!busy}
            data-testid="save-expense" className="cd-btn cd-btn-primary cd-btn-sm">
            {saving ? <><span className="cd-spinner" style={{ borderTopColor: '#fff' }} /> Ukladám...</>
              : <><I.check /> Uložiť</>}
          </button>
        }>
        <div style={{ padding: '14px 22px 0' }}>
          {/* Capture buttons */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <button
              type="button"
              onClick={() => setQrOpen(true)}
              disabled={!!busy}
              className="cd-btn cd-btn-primary cd-btn-sm"
              style={{ flex: 1, justifyContent: 'center' }}
            >
              <I.scan /> Naskenovať blok (QR)
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={!!busy}
              className="cd-btn cd-btn-ghost cd-btn-sm"
              style={{
                flex: 1, justifyContent: 'center',
                border: '1px solid var(--border)',
              }}
            >
              <I.camera /> Odfotiť doklad
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handlePhotoFile(file);
                e.target.value = '';
              }}
            />
          </div>

          {busyLabel && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 12px', marginBottom: 12,
              background: 'var(--product-primary-soft)',
              borderRadius: 'var(--r-md)',
              fontSize: 12.5, color: 'var(--product-primary-hover)',
            }}>
              <span className="cd-spinner" /> {busyLabel}
            </div>
          )}

          {docPreview && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: 8, marginBottom: 12,
              background: 'var(--bg-input)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)',
            }}>
              <img src={docPreview} alt="Doklad" style={{
                width: 56, height: 56, objectFit: 'cover',
                borderRadius: 'var(--r-sm)', flexShrink: 0,
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>
                  Doklad pripojený
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {docUrl ? 'Uložené v Drive' : 'Nahrávam...'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="cd-btn cd-btn-ghost cd-btn-sm"
              >
                Zmeniť
              </button>
            </div>
          )}
        </div>

        <div style={{ padding: '4px 22px 18px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Dodávateľ" full hint={aiHints.vendor_name}>
            <input className="cd-input" value={form.vendor_name} onChange={set('vendor_name')}
              placeholder="napr. Slovnaft a.s." data-testid="expense-vendor"
              style={hintStyle(aiHints.vendor_name)} />
          </Field>
          <Field label="IČO dodávateľa" full hint={aiHints.vendor_ico}>
            <input className="cd-input" value={form.vendor_ico} onChange={set('vendor_ico')}
              placeholder="8 číslic"
              style={hintStyle(aiHints.vendor_ico)} />
          </Field>
          <Field label="Suma (€)" hint={aiHints.amount}>
            <input className="cd-input mono" value={form.amount} onChange={set('amount')}
              placeholder="0,00" inputMode="decimal" data-testid="expense-amount"
              style={hintStyle(aiHints.amount)} />
          </Field>
          <Field label="Dátum" hint={aiHints.date}>
            <input className="cd-input mono" type="date" value={form.date} onChange={set('date')}
              data-testid="expense-date" style={hintStyle(aiHints.date)} />
          </Field>
          <Field label="DPH" hint={aiHints.dph_rate}>
            <div style={{ display: 'flex', gap: 4 }}>
              {DPH_RATES.map(r => (
                <button key={r}
                  onClick={() => { setForm(f => ({ ...f, dph_rate: r })); setAiHints(h => ({ ...h, dph_rate: false })); }}
                  className={`cd-chip${form.dph_rate === r ? ' active' : ''}`}
                  style={{ flex: 1 }}>{r}%</button>
              ))}
            </div>
          </Field>
          <Field label="Spôsob úhrady">
            <select className="cd-input" value={form.paid_via} onChange={set('paid_via')}
              style={{ padding: '9px 11px' }}>
              <option value="transfer">Bankový prevod</option>
              <option value="card">Karta</option>
              <option value="cash">Hotovosť</option>
              <option value="other">Iné</option>
            </select>
          </Field>
          <Field label="Popis" full hint={aiHints.description}>
            <input className="cd-input" value={form.description} onChange={set('description')}
              placeholder="napr. Číslo dokladu, spätný výkaz..."
              style={hintStyle(aiHints.description)} />
          </Field>

          {/* Category picker */}
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8,
            }}>Kategória</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {Object.entries(CATEGORIES).map(([key, c]) => {
                const active = key === form.category;
                return (
                  <button key={key} onClick={() => setForm(f => ({ ...f, category: key }))}
                    data-testid={`expense-cat-${key}`}
                    style={{
                      padding: '10px 8px',
                      background: active ? `var(--sec-${c.color}-soft)` : 'var(--bg-input)',
                      border: `1px solid ${active ? `var(--sec-${c.color})` : 'var(--border)'}`,
                      borderRadius: 'var(--r-md)',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                      cursor: 'pointer', fontFamily: 'inherit',
                      color: active ? `var(--sec-${c.color})` : 'var(--text-secondary)',
                    }}>
                    <SectionIcon color={c.color} size={28}>
                      {React.createElement(I[c.icon] || I.other, { width: 14, height: 14 })}
                    </SectionIcon>
                    <span style={{ fontSize: 11.5, fontWeight: active ? 600 : 500 }}>{c.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </ModalShell>

      <QrScanModal open={qrOpen} onClose={() => setQrOpen(false)} onScan={handleQrScan} />
    </>
  );
}

function hintStyle(hint) {
  if (!hint) return undefined;
  if (hint === 'ekasa') {
    return {
      borderColor: 'var(--product-primary)',
      boxShadow: '0 0 0 2px var(--product-primary-soft)',
    };
  }
  if (hint === 'ocr') {
    return {
      borderColor: '#a06bff',
      boxShadow: '0 0 0 2px rgba(160,107,255,0.15)',
    };
  }
  return {
    borderColor: 'var(--accent-info, #3a7bd5)',
    boxShadow: '0 0 0 2px rgba(58,123,213,0.15)',
  };
}

function Field({ label, full, hint, children }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto' }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <span>{label}</span>
        {hint && (
          <span style={{
            fontSize: 9, padding: '1px 6px', borderRadius: 'var(--r-full)',
            background:
              hint === 'ekasa' ? 'var(--product-primary-soft)' :
              hint === 'ocr' ? 'rgba(160,107,255,0.18)' :
              'rgba(58,123,213,0.18)',
            color:
              hint === 'ekasa' ? 'var(--product-primary-hover)' :
              hint === 'ocr' ? '#7038cc' :
              '#3a7bd5',
            textTransform: 'none', letterSpacing: 0,
          }}>
            {hint === 'ekasa' ? 'eKasa' : hint === 'ocr' ? '📖 OCR návrh' : '✨ AI návrh'}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}
